import {
  DocumentTemplate,
  DocumentTemplateWithEventName,
} from "@/types/DocumentTemplate";
import { apiGet, apiPost, apiPut, apiDelete, apiPostBlob, apiGetRaw } from "../_apiShim";
import type {
  CropResult,
  WalletCropRequest,
  WalletCropScope,
} from "@/types/WalletImageCrop";
import {
  builderIfMatchFrom,
  builderReadTicket,
  builderVersionFor,
  rememberBuilderVersion,
  settleBuilderVersion,
} from "./_builderVersionStore";

const BASE_PATH = "/api/v1/document-templates/";

export async function getDocumentTemplates(
  eventId: string,
): Promise<DocumentTemplate[]> {
  return await apiGet<DocumentTemplate[]>(BASE_PATH + eventId, {
    "Cache-Control": "no-store",
  }, true);
}

/**
 * Saved-template import for the creation wizard — mirrors the email
 * client's getEmailTemplatesByUserPaginated. Pass `eventId` to list ONLY
 * that event's templates ("this event" tab) or `excludeEventId` to list
 * every other accessible event's ("other events" tab).
 */
export async function getDocumentTemplatesByUserPaginated(params: {
  eventId?: string;
  excludeEventId?: string;
  documentType?: string;
  fields: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
}): Promise<DocumentTemplateWithEventName[]> {
  const query = new URLSearchParams();
  if (params.eventId) query.set("eventId", params.eventId);
  if (params.excludeEventId) query.set("excludeEventId", params.excludeEventId);
  if (params.documentType) query.set("documentType", params.documentType);
  query.set("fields", params.fields);
  query.set("page", String(params.page ?? 0));
  query.set("pageSize", String(params.pageSize ?? 9));
  query.set("sortBy", params.sortBy ?? "documentTemplateId");
  query.set("sortOrder", params.sortOrder ?? "desc");
  if (params.search) query.set("search", params.search);

  try {
    return await apiGet<DocumentTemplateWithEventName[]>(
      `${BASE_PATH}user/paginated?${query.toString()}`,
      {
        "Cache-Control": "no-store",
      },
      true,
    );
  } catch {
    return [];
  }
}

export async function getDocumentTemplate(
  eventId: string,
  documentTemplateId: string,
): Promise<DocumentTemplate> {
  // Ticket BEFORE the request: an answer a save overtook must not lower the
  // cached version (see _builderVersionStore).
  const ticket = builderReadTicket(documentTemplateId);
  const template = await apiGet<DocumentTemplate>(
    `${BASE_PATH}${eventId}/${documentTemplateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  // W2-F (M19): remember the loaded version so the save PUT can echo it
  // as If-Match (stale two-tab writes now 409 versionMismatch).
  rememberBuilderVersion(documentTemplateId, (template as { version?: number })?.version, ticket);
  return template;
}

/**
 * Download a real, signed wallet pass for one guest, for inspection.
 *
 * Server-rendered on purpose: the builder used to assemble this in the browser
 * and produced a file no device would install (no manifest, no signature). The
 * barcode carries a non-redeemable placeholder — an inspection artefact must
 * not double as a working door credential.
 */
export async function downloadWalletPreview(
  eventId: string,
  documentTemplateId: string,
  guestId: string,
): Promise<Blob> {
  const res = await apiGetRaw(
    `${BASE_PATH}${eventId}/${documentTemplateId}/wallet-preview/${guestId}`,
    { "Cache-Control": "no-store" },
  );
  return await res.blob();
}

/**
 * The "Add to Google Wallet" link for the same preview the `.pkpass` download
 * renders — the Google card ships as a BUTTON in the email, so a file download
 * has no counterpart here and there was previously no way to put the Google
 * card on a phone before sending it to guests.
 *
 * The link names an object in the server's `preview-` namespace with a
 * non-redeemable barcode: installing it cannot touch, or become, a guest's card.
 */
export async function getGoogleWalletPreviewUrl(
  eventId: string,
  documentTemplateId: string,
  guestId: string,
): Promise<string> {
  // POST, because minting is a WRITE: the server creates or updates a real
  // Google Wallet object under our issuer. As a GET it also slipped past the
  // backend's archived / plan-locked / tenant-blocked gates, which are all
  // keyed on the verb. No body — the three path segments are the request.
  const res = await apiPost<{ saveUrl?: string }>(
    `${BASE_PATH}${eventId}/${documentTemplateId}/google-wallet-preview/${guestId}`,
    {},
    { "Cache-Control": "no-store" },
    true,
  );
  const saveUrl = res?.saveUrl ?? "";
  if (!saveUrl) throw new Error("walletGooglePreviewUnavailable");
  return saveUrl;
}

export async function createDocumentTemplate(
  eventId: string,
  template: Partial<DocumentTemplate>,
): Promise<string> {
  return await apiPost<string>(`${BASE_PATH}${eventId}`, template, {
    "Cache-Control": "no-store",
  });
}

export async function updateDocumentTemplate(
  eventId: string,
  documentTemplateId: string,
  template: Partial<DocumentTemplate>,
): Promise<void> {
  const sent = builderVersionFor(documentTemplateId);
  await apiPut<void>(
    `${BASE_PATH}${eventId}/${documentTemplateId}`,
    template,
    {
      "Cache-Control": "no-store",
      // W2-F (M19): echo the loaded version — backend 409s stale writes.
      ...builderIfMatchFrom(sent),
    },
  );
  // Guarded PUT succeeded → the server is exactly at what we SENT + 1.
  settleBuilderVersion(documentTemplateId, sent);
}

export async function deleteDocumentTemplate(
  eventId: string,
  documentTemplateId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/${documentTemplateId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function bulkDeleteDocumentTemplates(
  eventId: string,
  documentTemplateIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/bulk-delete`,
    { documentTemplateIds },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function duplicateDocumentTemplate(
  eventId: string,
  documentTemplateId: string,
): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}${eventId}/${documentTemplateId}/duplicate`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function bulkDuplicateDocumentTemplates(
  eventId: string,
  documentTemplateIds: string[],
): Promise<DocumentTemplate[]> {
  return await apiPost<DocumentTemplate[]>(
    `${BASE_PATH}${eventId}/bulk-duplicate`,
    { documentTemplateIds },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * The download body plus the write-only `aiContentTool` when one is given
 * (plan A7/D2b). No tool ⇒ the very same object, no extra key.
 */
function withDownloadAiContentTool<T extends object>(
  body: T,
  aiContentTool: string | undefined,
): T | (T & { aiContentTool: string }) {
  return typeof aiContentTool === "string" && aiContentTool.length > 0 ? { ...body, aiContentTool } : body;
}

export async function downloadDocumentPdf(
  eventId: string,
  documentTemplateId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /**
   * Art. 50 Abs. 2 KI-VO (plan A7/D2b): the AI tool of an UNSAVED AI change
   * on the canvas these pages were rendered from. Write-only body field; the
   * server can only ADD the PDF's AI-Disclosure metadata from it, never remove
   * a stored mark. Absent ⇒ the body is byte-identical to the pre-mark call.
   */
  aiContentTool?: string,
): Promise<Uint8Array> {
  return await apiPostBlob(
    `${BASE_PATH}${eventId}/${documentTemplateId}/download-pdf`,
    withDownloadAiContentTool({ htmlPages, widthPx, heightPx, fileName }, aiContentTool),
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Per-guest PDF download: backend resolves `{{firstName}}`, `{{qrCodeImage}}`,
 * etc. against the named guest's payload + event signing key before rendering.
 * Use this when the operator wants a real ticket / certificate for a specific
 * guest. The plain `downloadDocumentPdf` (above) emits raw template HTML with
 * placeholders intact — it's a preview-only path.
 */
export async function downloadDocumentPdfForGuest(
  eventId: string,
  documentTemplateId: string,
  guestId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /** Art. 50 KI-VO: see `downloadDocumentPdf`. */
  aiContentTool?: string,
): Promise<Uint8Array> {
  return await apiPostBlob(
    `${BASE_PATH}${eventId}/${documentTemplateId}/download-pdf-for-guest/${guestId}`,
    withDownloadAiContentTool({ htmlPages, widthPx, heightPx, fileName }, aiContentTool),
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Batch per-guest PDF download. Server renders the template for every
 * guestId, packs the results into a single zip, returns it in one
 * response. Hard-capped at 50 guests at the server validation layer —
 * larger batches need the background-job path (not yet wired).
 */
export async function downloadDocumentPdfBatch(
  eventId: string,
  documentTemplateId: string,
  guestIds: string[],
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /** Art. 50 KI-VO: see `downloadDocumentPdf`. */
  aiContentTool?: string,
): Promise<Uint8Array> {
  return await apiPostBlob(
    `${BASE_PATH}${eventId}/${documentTemplateId}/download-pdf-batch`,
    withDownloadAiContentTool({ htmlPages, widthPx, heightPx, fileName, guestIds }, aiContentTool),
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function presignDocumentFile(
  eventId: string,
  documentTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string; fileId: string }> {
  return await apiPost<{ uploadUrl: string; s3Key: string; fileId: string }>(
    `${BASE_PATH}${eventId}/${documentTemplateId}/files/presign`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function confirmDocumentFile(
  eventId: string,
  documentTemplateId: string,
  data: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    targetField: string;
  },
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/${documentTemplateId}/files/confirm`,
    data,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteDocumentFile(
  eventId: string,
  documentTemplateId: string,
  fileId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/${documentTemplateId}/files/${fileId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Cut a wallet image slot to the operator's rectangle, on the server.
 *
 * Two lanes, one function: an event's template crops under
 * `/document-templates/{eventId}` (no document id — the create wizard calls
 * this before one exists) and a template-library entry under
 * `/template/document-template/{templateId}`. The server's DTO is flat
 * (`x, y, width, height`), the cockpit's `CropRect` is not; the mapping lives
 * here so neither side has to know the other's spelling.
 *
 * Deliberately NOT wrapped in a handler: the crop dialog reads the thrown
 * code (`walletCropTooSmall` is a sentence of its own, everything else goes
 * through `translateBackendError`), and `rethrowApi` puts exactly that code on
 * `error.message`. A handler that swallowed it into a toast would leave the
 * dialog open with nothing to say.
 */
export async function cropWalletImage(
  scope: WalletCropScope,
  body: WalletCropRequest,
): Promise<CropResult> {
  const path =
    "templateId" in scope
      ? `/api/v1/template/document-template/${scope.templateId}/wallet-images/crop`
      : `${BASE_PATH}${scope.eventId}/wallet-images/crop`;
  return await apiPost<CropResult>(
    path,
    {
      sourceUrl: body.sourceUrl,
      slot: body.slot,
      passType: body.passType,
      x: body.crop.x,
      y: body.crop.y,
      width: body.crop.w,
      height: body.crop.h,
    },
    { "Cache-Control": "no-store" },
    true,
  );
}
